import styles from "./vehicle-grid.module.css";
import VehicleFilter from "../Filter";
import VehicleCard from "./components/VehicleCard";
import { TableLoader } from "../../../../components/LoaderC";
import { TablePagination } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { toast } from "react-smart-toaster";
import MobileViewList from "../listview/component/MobileViewList";
import { buildFilterParams } from "../../vehicleQuery";
import { guestPassDisplay, useSendPaymentLink } from "../guestPass";

const isWithin30Days = (createdAt) => {
    if (!createdAt) return false;
    const createdDate = new Date(createdAt);
    const today = new Date();
    const diffInDays = (today - createdDate) / (1000 * 60 * 60 * 24);
    return diffInDays <= 30;
}

const formatPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');

    if (cleaned.length !== 10) return phone;

    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
}

const VehicleGridView = ({ isGridView, handleView, filterData, setFilterData, setAddModalOpen, setDeleteVehicleModalOpen, setSelectedId, setEditData, setEditModalOpen, setTenantModalOpen, setTenantDetails, query, refreshKey, onListChange }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [sendingId, sendPaymentLink] = useSendPaymentLink(onListChange);
    const [perPage, setPerPage] = useState(8);
    const [page, setPage] = useState(0);
    const [gridData, setGridData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [contentLoading, setContentLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [loadError, setLoadError] = useState(false);

    const requestSeq = useRef(0);
    const hasLoadedOnceRef = useRef(false);
    const lastQueryKeyRef = useRef(null);
    const loadedKeyRef = useRef(null);

    // One page of vehicles from the server (v1/vehicles/grid); search and filters run there too
    const fetchVehicles = useCallback(async () => {
        const seq = ++requestSeq.current;

        const isFirstLoad = !hasLoadedOnceRef.current;
        if (isFirstLoad) setContentLoading(true);
        else setRefetching(true);

        const params = { ...buildFilterParams(query), page: page + 1, per_page: perPage === -1 ? "all" : perPage };

        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/vehicles/grid", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: params,
            });

            if (seq !== requestSeq.current) return;

            const rows = response.data?.data || [];
            const meta = response.data?.meta || {};
            const total = meta?.total ?? 0;

            // The current page can become empty after a delete; move back to the last page that has data
            if (rows.length === 0 && page > 0 && total > 0) {
                setPage(Math.max(0, (meta?.last_page ?? 1) - 1));
                return;
            }

            setGridData(rows);
            setTotalCount(total);
            setLoadError(false);
            hasLoadedOnceRef.current = true;
        } catch (error) {
            if (seq !== requestSeq.current) return;
            console.error("[Vehicles] Failed to load vehicle grid", error);
            toast.error(error?.response?.data?.message || "Unable to load vehicles. Please try again.");

            // Allow the next activation of the grid view to try again
            loadedKeyRef.current = null;
            setLoadError(true);
            setGridData([]);
            setTotalCount(0);
        } finally {
            if (seq === requestSeq.current) {
                if (isFirstLoad) setContentLoading(false);
                else setRefetching(false);
            }
        }
    }, [getAccessTokenSilently, query, page, perPage]);

    // Load only while the grid view is visible, and only if search, filters, paging or data changed
    // since the last load (switching views back doesn't refetch). Filters are shared with the list
    // view, so a changed query first goes back to page 1, which then triggers a single request.
    const queryKey = JSON.stringify(query);
    const requestKey = JSON.stringify([queryKey, page, perPage, refreshKey]);
    useEffect(() => {
        if (!isGridView) return;

        const queryChanged = lastQueryKeyRef.current !== null && lastQueryKeyRef.current !== queryKey;
        lastQueryKeyRef.current = queryKey;
        if (queryChanged && page !== 0) {
            setPage(0);
            return;
        }

        if (loadedKeyRef.current === requestKey) return;
        loadedKeyRef.current = requestKey;
        fetchVehicles();
    }, [isGridView, queryKey, requestKey, page, fetchVehicles]);

    const rowsData = gridData.map((item) => {
        const isGuest = Number(item.is_guest) === 1 || item.is_guest === true || item.is_guest === 'true';

        if (isGuest) {
            const fname = item.guest_fname || "";
            const lname = item.guest_lname || "";
            const initials = (fname && lname) ? `${fname[0]}${lname[0]}`.toUpperCase() : "G";
            return {
                id: item.id,
                initials,
                name: `${fname} ${lname}`.trim(),
                phone: formatPhone(item.guest_phone || ""),
                license: item.license,
                make: item.make,
                model: item.model,
                color: item.color,
                property_id: item.property_id,
                property: item.property_name,
                is_disabled: item.is_disabled,
                unit: item.apt_no,
                tenant_image: "",
                tenant_id: item.tenant_id,
                tenant_info_id: item.tenant_info_id,
                apt_id: item.apt_id,
                isNew: isWithin30Days(item.created_at),
                image_path: item.image_path,
                image_thumbail_path: item.image_thumbail_path,
                is_guest: 1,
                // Latest parking pass from the server: raw for VehicleCard, display values for MobileViewList
                guest_pass: item.guest_pass,
                pass: guestPassDisplay(item.guest_pass),
                guest_fname: item.guest_fname || "",
                guest_lname: item.guest_lname || "",
                guest_phone: item.guest_phone || "",
                tenant_details: null
            };
        } else {
            return {
                id: item.id,
                initials: item.tenant_initial,
                name: item.tenant_name,
                license: item.license,
                make: item.make,
                model: item.model,
                color: item.color,
                property_id: item.property_id,
                property: item.property_name,
                is_disabled: item.is_disabled,
                unit: item.apt_no,
                tenant_image: item.tenant_image,
                tenant_id: item.tenant_id,
                tenant_info_id: item.tenant_info_id,
                apt_id: item.apt_id,
                isNew: isWithin30Days(item.created_at),
                image_path: item.image_path,
                image_thumbail_path: item.image_thumbail_path,
                is_guest: 0,
                tenant_details: { ...item.tenant_details, tenant_image: item.tenant_image, property: item.property_name, unit: item.apt_no }
            };
        }
    });

    return <div className={`${styles.BodyCon}`}>
        <div className={`${styles.mainCardDiv}`}>
            <div className={`${styles.mainCard}`}>

                <div className="control-pane">
                    <div className={`${styles.Row}`}>

                        <VehicleFilter isGridView={isGridView} handleView={handleView.bind(this)} filterData={filterData} setFilterData={setFilterData.bind(this)} setAddModalOpen={setAddModalOpen.bind(this)} />

                        {contentLoading && (<div className="Loader TableLoader"><TableLoader /></div>)}

                        {!contentLoading && (<div className={refetching ? styles.Refetching : undefined}>
                            <MobileViewList rowsData={rowsData} sendingId={sendingId} sendPaymentLink={sendPaymentLink} setSelectedId={setSelectedId.bind(this)} setDeleteVehicleModalOpen={setDeleteVehicleModalOpen.bind(this)} setEditData={setEditData.bind(this)} setEditModalOpen={setEditModalOpen.bind(this)} setTenantModalOpen={setTenantModalOpen.bind(this)} setTenantDetails={setTenantDetails.bind(this)} />

                            <div className={`${styles.NotesRow}`}>

                                {rowsData?.map((item) => (<VehicleCard
                                    key={item.id}
                                    item={item}
                                    showBadge={isWithin30Days(item.created_at)}
                                    sendingId={sendingId}
                                    sendPaymentLink={sendPaymentLink}
                                    setDeleteVehicleModalOpen={setDeleteVehicleModalOpen.bind(this)}
                                    setSelectedId={setSelectedId.bind(this)}
                                    setEditData={setEditData.bind(this)}
                                    setEditModalOpen={setEditModalOpen.bind(this)}
                                    setTenantModalOpen={setTenantModalOpen.bind(this)}
                                    setTenantDetails={setTenantDetails.bind(this)}
                                />))}

                                {totalCount > 0 && (<div className="Pagination TablePaginationNew List">
                                    <TablePagination
                                        count={totalCount}
                                        color="primary"
                                        page={page}
                                        rowsPerPage={perPage}
                                        onPageChange={(e, v) => setPage(v)}
                                        rowsPerPageOptions={[
                                            { label: '4', value: 4 },
                                            { label: '8', value: 8 },
                                            { label: '16', value: 16 },
                                            { label: 'All', value: -1 },
                                        ]}
                                        onRowsPerPageChange={(e) => {
                                            setPage(0);
                                            setPerPage(e.target.value);
                                        }}
                                        labelRowsPerPage={'Vehicles per Page:'}
                                    />
                                </div>)}

                                {loadError && (<div className={`${styles.NoDataMain}`}>
                                    <p className={`${styles.NoDataText}`}>Something went wrong!</p>
                                    <p className={`${styles.NoDataTextSub}`}>we couldn't load the vehicles.</p>
                                    <button type="button" className={`${styles.RetryBu}`} onClick={() => { loadedKeyRef.current = requestKey; fetchVehicles(); }}>Retry</button>
                                </div>)}

                                {!loadError && rowsData.length === 0 && (<div className={`${styles.NoDataMain}`}>
                                    <div className={`${styles.NoDataIMG}`}>
                                        <img src="/static/img/no-car.png" alt="No Data" />
                                    </div>
                                    <p className={`${styles.NoDataText}`}>Unfortunately!</p>
                                    <p className={`${styles.NoDataTextSub}`}>we couldn't find any vehicle at this time.</p>
                                </div>)}

                            </div>
                        </div>)}
                    </div>

                </div>
            </div>
        </div>
    </div>
}

export default VehicleGridView;
